/**
 * API клиент для привязки телефонов (Phone Binding)
 * Дата: 10.02.2026
 * Обновлено: 12.02.2026 — FIX: путь /phone-binding/ (без /admin/ — маршруты зарегистрированы в /api/)
 * Назначение: Вызов backend API для single/batch поиска через MTProto (contacts.importContacts)
 * Связан с: internal/handler/mass_phone_binding.go, hooks/usePhoneBinding.ts
 * 
 * Используется в:
 * - hooks/usePhoneBinding.ts — React хуки для компонентов
 * - components/messages/TelegramLinkPanel.tsx — inline привязка из чата (TODO-021b)
 * - pages/PhoneBinding.tsx — массовая привязка (TODO-021, TODO-021a)
 * 
 * Backend endpoints (cmd/api/main.go, строки 531-538):
 * - POST /api/phone-binding/single/search — поиск одного номера
 * - POST /api/phone-binding/batch/search — запуск batch поиска
 * - GET  /api/phone-binding/batch/jobs/:jobId — статус задачи
 * - GET  /api/phone-binding/stats — статистика поиска
 * - GET  /api/phone-binding/patients — список пациентов с привязками
 */
import { apiClient } from './client';

// ═══════════════════════════════════════════════════
// Типы запросов и ответов
// ═══════════════════════════════════════════════════

export interface PhoneSearchResult {
  phone: string;
  // 23.02.2026 — добавлен tg_client_unavailable (сессия MTProto истекла)
  status: 'found' | 'not_found' | 'rate_limited' | 'error' | 'tg_client_unavailable';
  telegram_user_id?: number;
  telegram_username?: string;
  first_name?: string;
  last_name?: string;
  privacy_flags?: {
    name_hidden: boolean;
    photo_hidden: boolean;
    phone_hidden: boolean;
  };
  error_message?: string;
  searched_at: string;
}

export interface SingleSearchRequest {
  phone: string;
}

export interface SingleSearchResponse {
  result: PhoneSearchResult;
}

export interface BatchSearchRequest {
  source: 'datalayer' | 'manual';
  phones?: string[];
  skip_already_found?: boolean;
  dry_run?: boolean;
}

export interface BatchSearchResponse {
  job_id: string;
  status: string;
}

export interface BatchJobStatus {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_phones: number;
  processed: number;
  found: number;
  not_found: number;
  errors: number;
  started_at: string;
  completed_at?: string;
}

export interface SearchStats {
  total_patients: number;
  bound_by_link: number;
  found_by_search: number;
  not_found: number;
  pending: number;
}

// ═══════════════════════════════════════════════════
// API методы
// ═══════════════════════════════════════════════════

export const phoneBindingApi = {
  /**
   * Поиск одного номера через TG Client (MTProto contacts.importContacts)
   * Endpoint: POST /api/phone-binding/single/search
   * 
   * Используется в TelegramLinkPanel для inline привязки из чата
   * Бэкенд возвращает PhoneSearchResult напрямую (не обёрнуто в {result: ...})
   */
  searchSingle: async (phone: string): Promise<PhoneSearchResult> => {
    const response = await apiClient.post('/phone-binding/single/search', { phone });
    return response.data;
  },

  /**
   * Запуск batch поиска
   * Endpoint: POST /api/phone-binding/batch/search
   */
  startBatchSearch: async (data: BatchSearchRequest): Promise<BatchSearchResponse> => {
    const response = await apiClient.post('/phone-binding/batch/search', data);
    return response.data;
  },

  /**
   * Получить статус batch задачи
   * Endpoint: GET /api/phone-binding/batch/jobs/:jobId
   */
  getJobStatus: async (jobId: string): Promise<BatchJobStatus> => {
    const response = await apiClient.get(`/phone-binding/batch/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Получить статистику поиска
   * Endpoint: GET /api/phone-binding/stats
   */
  getStats: async (): Promise<SearchStats> => {
    const response = await apiClient.get('/phone-binding/stats');
    return response.data;
  },
};
